import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, ShieldOff, Smartphone, Copy, CheckCheck, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../services/api";
import { useAdmin } from "../../context/AdminContext";

function ChangePasswordSection() {
    const [form, setForm]       = useState({ current: "", next: "", confirm: "" });
    const [show, setShow]       = useState(false);
    const [saving, setSaving]   = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (form.next.length < 6) { toast.error("Nové heslo musí mať aspoň 6 znakov"); return; }
        if (form.next !== form.confirm) { toast.error("Heslá sa nezhodujú"); return; }
        setSaving(true);
        try {
            await api.changeOwnPassword(form.current, form.next);
            toast.success("Heslo zmenené");
            setForm({ current: "", next: "", confirm: "" });
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-nova-surface/30 border border-white/8 rounded-2xl p-6">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-nova-cyan mb-5">/ Zmena hesla</h2>
            <form onSubmit={submit} className="space-y-3">
                {[
                    { key: "current", label: "Aktuálne heslo", ph: "••••••••" },
                    { key: "next",    label: "Nové heslo",      ph: "min. 6 znakov" },
                    { key: "confirm", label: "Potvrdiť heslo",  ph: "zopakujte nové heslo" },
                ].map(({ key, label, ph }) => (
                    <div key={key}>
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">{label}</label>
                        <div className="relative">
                            <input
                                type={show ? "text" : "password"}
                                value={form[key]}
                                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                                placeholder={ph}
                                className="w-full h-10 px-3 pr-9 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 transition-colors"
                            />
                            {key === "current" && (
                                <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                                    {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-nova-cyan text-nova-black font-semibold text-sm hover:bg-nova-cyan/90 transition-colors disabled:opacity-50"
                >
                    <KeyRound className="w-4 h-4" />
                    {saving ? "Ukladám…" : "Zmeniť heslo"}
                </button>
            </form>
        </div>
    );
}

export default function AdminSecurity() {
    const { user } = useAdmin();
    const [status, setStatus]       = useState(null);
    const [step, setStep]           = useState("idle"); // idle | setup | verify | disable
    const [qrData, setQrData]       = useState(null);
    const [secret, setSecret]       = useState("");
    const [code, setCode]           = useState("");
    const [loading, setLoading]     = useState(false);
    const [copied, setCopied]       = useState(false);

    const reload = () => api.me().then(d => setStatus(d)).catch(() => {});
    useEffect(() => { reload(); }, []);

    const startSetup = async () => {
        setLoading(true);
        try {
            const data = await api.setup2fa();
            setQrData(data.qr_data_url);
            setSecret(data.secret);
            setStep("setup");
            setCode("");
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const confirmEnable = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.enable2fa(code);
            toast.success("Dvojfaktorová autentifikácia aktivovaná");
            setStep("idle");
            setCode("");
            reload();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const confirmDisable = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.disable2fa(code);
            toast.success("Dvojfaktorová autentifikácia deaktivovaná");
            setStep("idle");
            setCode("");
            reload();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const copySecret = () => {
        navigator.clipboard.writeText(secret);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const twoFaEnabled = status?.totp_enabled;

    return (
        <div className="max-w-lg space-y-6">
            <div className="mb-8">
                <h1 className="font-heading text-2xl font-black text-white tracking-tight">Zabezpečenie účtu</h1>
                <p className="text-zinc-500 text-sm mt-1">Nastavenia dvojfaktorovej autentifikácie pre <span className="text-white">{user?.username}</span>.</p>
            </div>

            {/* Status card */}
            <div className="bg-nova-surface/30 border border-white/8 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${twoFaEnabled ? "bg-green-400/10 border border-green-400/20" : "bg-zinc-700/30 border border-white/8"}`}>
                        {twoFaEnabled
                            ? <ShieldCheck className="w-5 h-5 text-green-400" />
                            : <ShieldOff className="w-5 h-5 text-zinc-500" />
                        }
                    </div>
                    <div className="flex-1">
                        <div className="text-white font-semibold text-sm">
                            {twoFaEnabled ? "2FA je aktívna" : "2FA nie je zapnutá"}
                        </div>
                        <div className="text-zinc-500 text-xs mt-1">
                            {twoFaEnabled
                                ? "Váš účet je chránený dvojfaktorovou autentifikáciou (TOTP)."
                                : "Zapnite 2FA pre zvýšenú bezpečnosť vášho admin účtu."
                            }
                        </div>
                    </div>
                    {step === "idle" && (
                        <button
                            onClick={twoFaEnabled ? () => { setStep("disable"); setCode(""); } : startSetup}
                            disabled={loading}
                            className={`shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${
                                twoFaEnabled
                                    ? "bg-red-500/10 text-red-400 border border-red-400/20 hover:bg-red-500/20"
                                    : "bg-nova-cyan text-black hover:bg-nova-cyan/90"
                            }`}
                        >
                            {loading ? "…" : twoFaEnabled ? "Vypnúť" : "Zapnúť"}
                        </button>
                    )}
                </div>
            </div>

            {/* Setup flow */}
            {step === "setup" && qrData && (
                <div className="bg-nova-surface/30 border border-white/8 rounded-2xl p-6 space-y-5">
                    <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-nova-cyan">/ Nastavenie 2FA</h2>
                    <div className="flex flex-col sm:flex-row gap-6 items-start">
                        <div className="shrink-0">
                            <img src={qrData} alt="QR kód" className="w-40 h-40 rounded-lg border border-white/10" />
                        </div>
                        <div className="space-y-3 flex-1">
                            <div className="flex items-center gap-2 text-zinc-400 text-xs">
                                <Smartphone className="w-4 h-4 shrink-0" />
                                Naskenujte QR kód v aplikácii Google Authenticator alebo Authy.
                            </div>
                            <div>
                                <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Manuálny kód</div>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 text-xs font-mono bg-nova-black/60 border border-white/8 rounded-lg px-3 py-2 text-nova-cyan break-all">
                                        {secret}
                                    </code>
                                    <button onClick={copySecret} className="shrink-0 p-2 rounded-lg border border-white/8 text-zinc-500 hover:text-white transition-colors">
                                        {copied ? <CheckCheck className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={confirmEnable} className="space-y-3 pt-2 border-t border-white/8">
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Overovacie kód z aplikácie</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={7}
                                value={code}
                                onChange={e => setCode(e.target.value)}
                                placeholder="000000"
                                className="w-full h-12 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-xl font-mono text-center tracking-[0.4em] outline-none focus:border-nova-cyan/40 transition-colors"
                                autoFocus
                            />
                        </div>
                        <div className="flex gap-3">
                            <button type="button" onClick={() => setStep("idle")} className="flex-1 h-10 rounded-xl border border-white/10 text-zinc-400 hover:text-white text-sm transition-colors">
                                Zrušiť
                            </button>
                            <button type="submit" disabled={loading} className="flex-1 h-10 rounded-xl bg-nova-cyan text-black font-semibold text-sm hover:bg-nova-cyan/90 disabled:opacity-50 transition-colors">
                                {loading ? "Overujem…" : "Aktivovať 2FA"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Disable flow */}
            {step === "disable" && (
                <div className="bg-nova-surface/30 border border-white/8 rounded-2xl p-6 space-y-4">
                    <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-400">/ Vypnutie 2FA</h2>
                    <p className="text-zinc-400 text-sm">Pre potvrdenie zadajte aktuálny kód z autentifikátora.</p>
                    <form onSubmit={confirmDisable} className="space-y-3">
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={7}
                            value={code}
                            onChange={e => setCode(e.target.value)}
                            placeholder="000000"
                            className="w-full h-12 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-xl font-mono text-center tracking-[0.4em] outline-none focus:border-red-400/40 transition-colors"
                            autoFocus
                        />
                        <div className="flex gap-3">
                            <button type="button" onClick={() => setStep("idle")} className="flex-1 h-10 rounded-xl border border-white/10 text-zinc-400 hover:text-white text-sm transition-colors">
                                Zrušiť
                            </button>
                            <button type="submit" disabled={loading} className="flex-1 h-10 rounded-xl bg-red-500 text-white font-semibold text-sm hover:bg-red-400 disabled:opacity-50 transition-colors">
                                {loading ? "Deaktivujem…" : "Vypnúť 2FA"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Change own password */}
            <ChangePasswordSection />
        </div>
    );
}
